import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

/**
 *  ###1) 그리드 상단 제목영역/상단 render영역은 각 화면으로 이동한다(6/12)
 *
 *  ###2) 그리드에서도 UI 컴포넌트는 Sat공통 컴포넌트를 동일하게 사용한다.(column custom render시)
 *
 *   #### -- input/button/checkbox/select/radio/datepicker 등
 */
const SatDataGridBtfDnd = forwardRef((props, ref) => {
  const {
    columns,
    dataSource,
    sortingOption,
    setRowStyle,
    customStyleClass,
    noDataRender,
    dndHeightOption = '',
    onSortChange,
    onReSorting,
    droppableId,
    isDragDisabled = false,
    onMouseDown,
    onTouchStart,
  } = props;

  const [rowData, setRowData] = useState([]);
  const [dndHeight, setDndHeight] = useState(dndHeightOption);

  useImperativeHandle(ref, () => ({
    // 그리드의 현재 행 데이터(rowData)
    getRowData() {
      return rowData;
    },
  }));

  useEffect(() => {
    setRowData(
      dataSource.map((data, index) => {
        return { ...data, rowIndex: index };
      }),
    );

    setDndHeight(dndHeightOption);
  }, []);

  useEffect(() => {
    setRowData(
      dataSource.map((data, index) => {
        return { ...data, rowIndex: index };
      }),
    );
    setDndHeight(dndHeightOption);
  }, [dataSource]);

  const getRowStyle = row => {
    if (typeof setRowStyle === 'function') {
      return setRowStyle(row);
    } else if (typeof setRowStyle === 'string') {
      return setRowStyle;
    } else {
      return '';
    }
  };

  const HeaderRender = ({ column }) => {
    const { dataIndex, sortable = false, title, width = '' } = column;

    const getSortOption = () => {
      const sortOption = sortingOption.find(
        option => option.sortBy === dataIndex,
      );
      if (sortable && sortOption) {
        return sortOption.order === 'asc' ? 'sort-down up' : 'sort-down';
      } else {
        return '';
      }
    };

    const handleHeaderSorting = () => {
      const sortOption = sortingOption.find(
        option => option.sortBy === dataIndex,
      );

      if (sortOption) {
        onSortChange({
          ...sortOption,
          order: sortOption.order === 'asc' ? 'desc' : 'asc',
        });
      }
    };

    return (
      <span
        className={getSortOption()}
        style={{ width: width }}
        onClick={handleHeaderSorting}
      >
        {title}
      </span>
    );
  };

  const CellRender = ({ row, column, dragHandleProps }) => {
    const { dataIndex, render, width, colSpan = undefined, draggable } = column;
    const cellRenderer = (
      <>
        {typeof render === 'function' ? (
          <>
            {draggable ? (
              <div className="box-gift" {...dragHandleProps}>
                {draggable(row[dataIndex], row) && (
                  <span className="ico ico-3wire" />
                )}
                {render(row[dataIndex], row)}
              </div>
            ) : (
              render(row[dataIndex], row)
            )}
          </>
        ) : (
          row[dataIndex]
        )}
      </>
    );

    return (
      <div
        className={`td-cell ${
          colSpan &&
          typeof colSpan === 'function' &&
          colSpan(row[dataIndex], row) > 1
            ? 'w100'
            : ''
        }`}
        style={{ width: typeof width === 'function' ? width(row) : width }}
      >
        {cellRenderer}
      </div>
    );
  };

  const handleDragEnd = props => {
    const {
      source: { index: sIndex },
      destination: { index: dIndex },
    } = props;

    if (sIndex !== dIndex) onReSorting(sIndex, dIndex);
  };

  const rowSpanRender = row => {
    const rowSpanColumn = columns.find(column => column['rowSpanRender']);
    if (rowSpanColumn) {
      return <div className="tr">{rowSpanColumn.rowSpanRender(row)}</div>;
    } else {
      return <></>;
    }
  };

  return (
    <React.Fragment>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="tbl-style special">
          <div className="thead">
            {columns.map((column, index) => (
              <HeaderRender key={index} column={column} />
            ))}
          </div>
          <Droppable droppableId={droppableId}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                className="tbody"
                style={{ height: dndHeight }}
              >
                {rowData.length > 0 ? (
                  rowData.map((data, index) => (
                    <Draggable
                      isDragDisabled={isDragDisabled}
                      key={data.key}
                      draggableId={data.key}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          key={data.key}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          onMouseDown={e => {
                            // 드래그 비활성화 상태에서 이벤트 별도 관리
                            if (isDragDisabled) {
                              e.preventDefault();
                              onMouseDown(e, data);
                            }
                          }}
                          onTouchStart={e => {
                            // 드래그 비활성화 상태에서 이벤트 별도 관리
                            if (isDragDisabled) {
                              e.preventDefault();
                              onTouchStart(e, data);
                            }
                          }}
                        >
                          <div className={`tr ${getRowStyle(data)}`}>
                            {columns.map((columnInfo, index) => {
                              const { dataIndex } = columnInfo;
                              const colSpanRows = columns.filter(
                                col =>
                                  Object.keys(col).includes('colSpan') &&
                                  col.colSpan(data[dataIndex], data) > 1,
                              );

                              if (colSpanRows.length > 0) {
                                const colSpanIndex = columns.findIndex(col =>
                                  Object.keys(col).includes('colSpan'),
                                );

                                if (index === colSpanIndex) {
                                  return (
                                    <CellRender
                                      key={index}
                                      row={data}
                                      column={columnInfo}
                                      dragHandleProps={{
                                        ...provided.dragHandleProps,
                                      }}
                                    />
                                  );
                                }
                              } else {
                                return (
                                  <CellRender
                                    key={index}
                                    row={data}
                                    column={columnInfo}
                                    dragHandleProps={{
                                      ...provided.dragHandleProps,
                                    }}
                                  />
                                );
                              }
                            })}
                          </div>
                          {rowSpanRender(data)}
                          {data.children &&
                            data.children.map((child, index) => {
                              return (
                                <>
                                  <div
                                    key={index}
                                    className={`tr ${getRowStyle(child)}`}
                                  >
                                    {columns.map((columnInfo, index) => {
                                      return (
                                        <CellRender
                                          key={index}
                                          row={child}
                                          column={columnInfo}
                                        />
                                      );
                                    })}
                                  </div>
                                  {rowSpanRender(child)}
                                </>
                              );
                            })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Draggable>
                  ))
                ) : (
                  <div className="txt-nodata">
                    {typeof noDataRender === 'string' && noDataRender}
                    {typeof noDataRender === 'function' && noDataRender()}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </React.Fragment>
  );
});

SatDataGridBtfDnd.propTypes = {
  /**
   * 그리드 칼럼
   */
  columns: PropTypes.array,
  /**
   * 그리드 데이터
   */
  dataSource: PropTypes.array,
  /**
   * 행 style
   */
  setRowStyle: PropTypes.any,
  /**
   * sorting Option
   */
  sortingOption: PropTypes.array,
  /**
   * table class 추가
   */
  customStyleClass: PropTypes.string,
  /**
   * 그리드 데이터 없을 시 메세지
   */
  noDataRender: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * drag & drop option
   */
  draggable: PropTypes.bool,
  /**
   * 행 선택(rowSelection) callback event
   */
  onRowSelection: PropTypes.func,
  /**
   * 헤더 정렬 헤더 클릭시 callback event
   */
  onSortChange: PropTypes.func,
  /**
   * 입력값 callback event
   */
  onChange: PropTypes.func,
};

SatDataGridBtfDnd.defaultProps = {
  columns: [],
  dataSource: [],
  setRowStyle: () => {},
  sortingOption: [],
  draggable: false,
  customStyleClass: '',
  onRowSelection: () => {},
  onSortChange: () => {},
  onChange: () => {},
};

export default SatDataGridBtfDnd;
